import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VideoPopup from './VideoPopup';
import '../App.css';

// Smart API URL switching
const API_BASE_URL =
    window.location.hostname === "staging187611.digassistant.xyz"
        ? "https://api.digassistant.xyz"
        : window.location.hostname === "digassistant.xyz" || window.location.hostname === "www.digassistant.xyz"
            ? "https://api.digassistant.xyz"
            : "http://localhost:8080";

const DiveSessionHistoryOverlay = ({ onClose, isVisible }) => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [videosByReleaseId, setVideosByReleaseId] = useState({});
    const [selectedVideoPopup, setSelectedVideoPopup] = useState(null);

    useEffect(() => {
        if (isVisible) {
            fetchDiveSessionHistory();
        }
    }, [isVisible]);

    const fetchDiveSessionHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/dive/history`, {
                withCredentials: true
            });
            const sessionData = response.data.session;
            setSession(sessionData);

            // Fetch videos for seed release and all events
            if (sessionData) {
                const releaseIds = [
                    sessionData.seed_release_id,
                    ...sessionData.events.map(e => e.event_release_id)
                ];

                const videoPromises = releaseIds.map(async (releaseId) => {
                    try {
                        const videoResponse = await fetch(`${API_BASE_URL}/videos/${releaseId}`, {
                            credentials: 'include'
                        });
                        if (videoResponse.ok) {
                            const data = await videoResponse.json();
                            if (data?.videos?.video && data.videos.video.length > 0) {
                                setVideosByReleaseId(prev => ({
                                    ...prev,
                                    [releaseId]: data.videos.video
                                }));
                            }
                        }
                    } catch (err) {
                        // Silently fail - no videos for this release
                    }
                });

                await Promise.allSettled(videoPromises);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch dive session history');
            console.error('Error fetching dive session history:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    if (!isVisible) {
        return null;
    }

    const calculateTimeInWormhole = (startedAt) => {
        if (!startedAt) return 0;
        const start = new Date(startedAt);
        const now = new Date();
        return Math.floor((now - start) / 1000); // Convert milliseconds to seconds
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0s';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const RecommendationLink = ({ releaseId, artist, title, videos, onVideoClick }) => {
        const hasVideos = videos && videos.length > 0;

        const handleVideoClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (hasVideos) {
                onVideoClick({
                    releaseId,
                    artist,
                    title,
                    videos
                });
            }
        };

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <a
                    href={`https://www.discogs.com/release/${releaseId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#fff'}
                    onMouseLeave={(e) => e.target.style.color = 'inherit'}
                >
                    {artist} - {title}
                </a>
                {hasVideos && (
                    <button
                        className="video-indicator-button"
                        onClick={handleVideoClick}
                    >
                        <img
                            src="https://storage.googleapis.com/tmhimagebucket/youtubelogogreen.svg"
                            alt=">>"
                        />
                    </button>
                )}
            </span>
        );
    };

    return (
        <div className="welcome-overlay" onClick={handleClose}>
            {selectedVideoPopup && (
                <VideoPopup
                    videos={selectedVideoPopup.videos}
                    releaseId={selectedVideoPopup.releaseId}
                    artist={selectedVideoPopup.artist}
                    title={selectedVideoPopup.title}
                    discogsUrl={`https://www.discogs.com/release/${selectedVideoPopup.releaseId}`}
                    onClose={() => setSelectedVideoPopup(null)}
                />
            )}
            <div className="welcome-overlay-content" style={{ maxWidth: '600px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
                <button className="welcome-close-button" onClick={handleClose}>×</button>
                <h2>Dive Session History</h2>

                {loading && <p>Loading...</p>}

                {error && (() => {
                    // Hyperlink last 3 words for rate limit errors
                    if (error.includes("becoming a member")) {
                        const words = error.trim().split(/\s+/);
                        if (words.length >= 3) {
                            const lastThreeWords = words.slice(-3).join(' ');
                            const restOfMessage = words.slice(0, -3).join(' ');
                            return (
                                <p style={{ color: 'inherit' }}>
                                    {restOfMessage}
                                    {restOfMessage && ' '}
                                    <a
                                        href="/upgrade"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = '/upgrade';
                                        }}
                                        style={{
                                            color: '#009e4a',
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {lastThreeWords}
                                    </a>
                                </p>
                            );
                        }
                    }
                    // Default error display (not red)
                    return <p style={{ color: 'inherit' }}>{error}</p>;
                })()}

                {!loading && !error && !session && (
                    <p>No active dive session found.</p>
                )}

                {!loading && !error && session && (
                    <div style={{ textAlign: 'left', marginTop: '20px', maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ marginBottom: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                                Initial wormhole release
                            </div>
                            <div style={{ marginLeft: '15px', fontSize: '0.9em' }}>
                                <RecommendationLink
                                    releaseId={session.seed_release_id}
                                    artist={session.seed_artist}
                                    title={session.seed_title}
                                    videos={videosByReleaseId[session.seed_release_id] || []}
                                    onVideoClick={setSelectedVideoPopup}
                                />
                            </div>
                        </div>

                        {session.events.length === 0 ? (
                            <p style={{ fontStyle: 'italic', color: '#666' }}>No dive events yet.</p>
                        ) : (
                            <div style={{ marginTop: '20px' }}>
                                <div style={{ marginBottom: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                                    Current trajectory
                                </div>
                                {session.events.map((event, index) => (
                                    <div key={index} style={{ marginBottom: '10px', fontSize: '0.9em', marginLeft: '15px' }}>
                                        <span style={{ marginRight: '10px' }}>→</span>
                                        <RecommendationLink
                                            releaseId={event.event_release_id}
                                            artist={event.event_artist}
                                            title={event.event_title}
                                            videos={videosByReleaseId[event.event_release_id] || []}
                                            onVideoClick={setSelectedVideoPopup}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ marginTop: '20px', fontSize: '0.85em', color: '#009e4a' }}>
                            Total time in wormhole: {formatDuration(calculateTimeInWormhole(session.started_at))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DiveSessionHistoryOverlay;

